// mui
import Box from '@mui/material/Box';

// icons
import type { IconWeight } from '@phosphor-icons/react';
import {
  brandIcons,
  iconGlyphs,
  strokeOnlyIcons,
  type Icons,
} from '@/components/atoms/Icon/icons';
import { resolveIcon } from '@/components/atoms/Icon/resolveIcon';

// theme
import { spacing } from '@/theme/spacing';
import { Color } from '@/theme/color';
import { elevation } from '@/theme/elevation';

// utils
import { getColorContrast } from '@/utils/color';

/**
 * IconType selects the weight. The names are inherited from FontAwesome so that
 * existing call sites keep working.
 */
export type IconType = 'fas' | 'fal' | 'fab';

/**
 * weightFor maps a FontAwesome family onto the Phosphor weight that draws the
 * same glyph at the same visual weight.
 *
 * `fal` is the design system's default and was FontAwesome Light, whose stroke
 * Phosphor's `light` matches; `regular` here would thicken every outlined icon
 * in the app. `fas` was Solid, which is Phosphor's `fill` for a glyph with an
 * area to fill and `bold` for one that is only strokes (see `strokeOnlyIcons`),
 * where `fill` would swap the drawing for a knocked-out block. Because Phosphor
 * varies one glyph rather than shipping separate families, an icon keeps its
 * shape across weights instead of changing to a different drawing.
 */
const weightFor = (icon: Icons, type: IconType): IconWeight => {
  // a brand mark is always filled — a logo drawn at a line weight reads as a
  // sketch of the logo rather than the logo itself
  if (brandIcons.has(icon)) return 'fill';
  if (type === 'fal') return 'light';
  return strokeOnlyIcons.has(icon) ? 'bold' : 'fill';
};

/**
 * IconSize mirrors the size tokens the design system used with FontAwesome, so
 * call sites did not have to change when the icons moved to Phosphor.
 */
export type IconSize =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'lg'
  | 'xl'
  | '2xl'
  | '1x'
  | '2x'
  | '3x'
  | '4x'
  | '5x'
  | '6x'
  | '7x'
  | '8x'
  | '9x'
  | '10x';

/**
 * sizeEm maps a size token onto the em value FontAwesome's sizing class of the
 * same name applied, so an icon keeps scaling with the text it sits in: `sm`
 * inside a 14px button is smaller than `sm` inside an 18px heading. Phosphor's
 * default size is `1em`, so an icon with no size token behaves the same way.
 */
const sizeEm: Record<IconSize, string> = {
  '2xs': '0.625em',
  xs: '0.75em',
  sm: '0.875em',
  lg: '1.25em',
  xl: '1.5em',
  '2xl': '2em',
  '1x': '1em',
  '2x': '2em',
  '3x': '3em',
  '4x': '4em',
  '5x': '5em',
  '6x': '6em',
  '7x': '7em',
  '8x': '8em',
  '9x': '9em',
  '10x': '10em',
};

/**
 * paddingFor is the inset between a glyph and the edge of its filled circle,
 * stepped with the glyph's size so the circle keeps the same proportions.
 */
const paddingFor = (size?: IconSize, fontSize?: number): string => {
  let padding = '16px';
  switch (size) {
    case '2xs':
    case 'xs':
      padding = spacing[4];
      break;
    case 'sm':
      padding = spacing[8];
      break;
    case 'lg':
      padding = spacing[12];
      break;
    case 'xl':
      padding = spacing[16];
      break;
    case '2xl':
    case '2x':
      padding = spacing[20];
      break;
    case '1x':
      padding = spacing[8];
      break;
    case '3x':
      padding = spacing[24];
      break;
    case '4x':
      padding = spacing[36];
      break;
    case '5x':
    case '6x':
    case '7x':
    case '8x':
    case '9x':
    case '10x':
      padding = spacing[48];
      break;
    default:
      padding = spacing[8];
      break;
  }

  if (fontSize) {
    if (fontSize < 12) {
      padding = spacing[4];
    } else if (fontSize < 24) {
      padding = spacing[8];
    } else if (fontSize < 32) {
      padding = spacing[12];
    } else if (fontSize < 44) {
      padding = spacing[16];
    } else if (fontSize < 64) {
      padding = spacing[20];
    } else if (fontSize < 82) {
      padding = spacing[24];
    } else if (fontSize < 112) {
      padding = spacing[36];
    } else if (fontSize < 192) {
      padding = spacing[48];
    } else if (fontSize >= 192) {
      padding = spacing[64];
    }
  }
  return padding;
};

export interface IconProps {
  className?: string;
  icon: Icons;
  type?: IconType;
  size?: IconSize;
  fontSize?: number;
  isFlip?: boolean;
  fill?: Color;
  color?: string;
  isElevation?: boolean;
}

/**
 * Icon displays an icon.
 */
export const Icon = ({
  className,
  icon,
  size,
  fontSize,
  isFlip,
  color,
  type = 'fal',
  fill,
  isElevation,
}: IconProps) => {
  // a name that is not a registry key can still arrive here, as free text from
  // a record cast to `Icons` by its caller. It resolves through the alias table
  // and falls back to a neutral glyph with a dev warning, rather than leaving a
  // silent gap in the layout.
  const name = icon in iconGlyphs ? icon : resolveIcon(icon);
  const Component = iconGlyphs[name];

  const glyph = (
    <Component
      className={className}
      weight={weightFor(name, type)}
      size={fontSize ?? (size ? sizeEm[size] : undefined)}
      // Phosphor mirrors a glyph itself, so the flip survives a transform on an
      // ancestor and does not need one of its own
      mirrored={isFlip}
      // the glyph is drawn in `currentColor`, so a colour is set as CSS on the
      // svg rather than passed to Phosphor, which would write it straight to the
      // `fill` attribute: a value that is not a CSS colour is then ignored and
      // the icon inherits its parent's colour, as it always has. Inside a filled
      // circle the glyph takes the contrasting colour set on the wrapper, so an
      // explicit colour only applies outside one.
      style={color && !fill ? { color } : undefined}
    />
  );

  // a filled icon sits in a contrasting circle. Phosphor glyphs are drawn on a
  // square grid, so the circle only needs an aspect ratio — unlike FontAwesome,
  // which needed a hidden spacer glyph to square up variable-width icons.
  if (fill) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '99999px',
          padding: paddingFor(size, fontSize),
          backgroundColor: fill,
          color: getColorContrast(fill),
          width: 'min-content',
          aspectRatio: '1 / 1',
          boxShadow: isElevation ? elevation[4] : undefined,
        }}
      >
        {glyph}
      </Box>
    );
  }

  return glyph;
};

Icon.displayName = 'Icon';

export default Icon;
